import { useState, useRef, useEffect } from "react";
import Button from "../../components/ui/Button";
import { ArrowUp, Loader } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import {
  generateLayoutFromPrompt,
  generateDescriptionsFromPrompt,
} from "../../api/agentApi";
import { getPresentationFile } from "../../api/presentationApi";
import { dynamicSections, sectionMapper } from "../../utils";
import CardList from "./CardList";
import { useSnackbar } from "notistack";
import ClientDetails from "./Sections/ClientDetails";
import SidebarButton from "../../components/ui/SidebarButton";
import { getCaseStudiesFromPrompt } from "../../api/contentApi";

const ELIXIRR_VALUE_SLIDES = [
  {
    id: "elixirr_1",
    title: "Elixirr Value Slide 1",
    thumbnail: "/slideThumbnails/elixirrValue/s2.jpg",
  },
  {
    id: "elixirr_2",
    title: "Elixirr Value Slide 2",
    thumbnail: "/slideThumbnails/elixirrValue/s1.jpg",
  },
  {
    id: "elixirr_3",
    title: "Elixirr Value Slide 3",
    thumbnail: "/slideThumbnails/elixirrValue/s3.jpg",
  },
  {
    id: "elixirr_5",
    title: "Elixirr Value Slide 4",
    thumbnail: "/slideThumbnails/elixirrValue/s4.jpg",
  },
];

export default function ProposalCreator() {
  const { enqueueSnackbar } = useSnackbar();
  const initialInputRef = useRef(null);
  const isAborted = useRef(false);
  const downloadButton = useRef(null);

  const [input, setInput] = useState("");
  const [slides, setSlides] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [elixirrValueSelectedSlides, setElixirrValueSelectedSlides] = useState(
    []
  );
  const [caseStudiesSelectedSlides, setCaseStudiesSelectedSlides] = useState(
    []
  );
  const [caseStudiesSlides, setCaseStudiesSlides] = useState([]);
  const [proposalSettings, setProposalSettings] = useState({
    client: "",
    industry: "",
    logoFile: null,
    contactPersons: [{ name: "", email: "", role: "" }],
  });
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (initialInputRef.current && step === 0) {
      initialInputRef.current.innerText = input;
    }
    if (step === 0 && downloadButton.current) {
      console.log(downloadButton.current);
      downloadButton.current.innerHTML = "";
    }
  }, [step]);

  const handleAddSlide = (section) => {
    setSlides({
      ...slides,
      [section]: [
        ...slides[section],
        { description: "Explain the slide..", id: uuidv4() },
      ],
    });
  };

  const showErrorMessage = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const sourceSlides = Array.from(slides[result.source.droppableId]);
    if (result.source.droppableId === result.destination.droppableId) {
      const [removed] = sourceSlides.splice(result.source.index, 1);
      sourceSlides.splice(result.destination.index, 0, removed);
      setSlides({
        ...slides,
        [result.source.droppableId]: sourceSlides,
      });
      return;
    }
    const destinationSlides = Array.from(
      slides[result.destination.droppableId]
    );
    const [removed] = sourceSlides.splice(result.source.index, 1);
    destinationSlides.splice(result.destination.index, 0, removed);
    setSlides({
      ...slides,
      [result.source.droppableId]: sourceSlides,
      [result.destination.droppableId]: destinationSlides,
    });
  };

  function getImageDimensionsFromBase64(base64) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = (error) => reject(error);
    });
  }

  const preprocessAndGenerateFile = async (data) => {
    const finalSlides = [];

    // add section break slide to each section
    Object.keys(data).forEach((section, index) => {
      if (index > 0) {
        finalSlides.push({
          id: "sectionBreakSlide",
          title: sectionMapper[section],
          subtitle: "",
          sectionNumber: index < 10 ? `0${index}` : index,
        });
      }
      data[section].slides.forEach((slide) => finalSlides.push(slide));
    });

    // add uploaded logo to initial slide
    if (finalSlides?.length && finalSlides[0].id === "initialSlide") {
      if (proposalSettings.logoBase64) {
        const imageDimensions = await getImageDimensionsFromBase64(
          proposalSettings.logoBase64
        );
        finalSlides[0].client = {
          name: proposalSettings.client,
          logo: {
            data: proposalSettings.logoBase64,
            h: imageDimensions.height,
            w: imageDimensions.width,
          },
        };
      }
    }

    // add value proposition selected slides
    if (finalSlides?.length && elixirrValueSelectedSlides.length) {
      const insertIndex = finalSlides.findIndex(
        (slide) => slide.id === "requirementsSlide"
      );
      if (insertIndex !== -1) {
        finalSlides.splice(
          insertIndex + 1,
          0,
          ...[
            {
              id: "sectionBreakSlide",
              title: "Elixirr value proposition",
              subtitle: "",
              sectionNumber: "02",
            },
            ...elixirrValueSelectedSlides.map((slideId) => ({
              id: slideId,
            })),
          ]
        );
      }
    }
    if (caseStudiesSelectedSlides.length) {
      console.log("case studies selected", caseStudiesSelectedSlides);
      console.log("case studies slides", caseStudiesSlides);
      finalSlides.push({
        id: "sectionBreakSlide",
        title: "Case Studies",
        subtitle: "",
        sectionNumber: "05",
      });
      caseStudiesSelectedSlides.forEach((slideId) => {
        const image = caseStudiesSlides.find(
          (slide) => slide.id === slideId
        )?.image;
        finalSlides.push({
          id: "caseStudyImageSlide",
          image: image,
        });
      });
      setCaseStudiesSelectedSlides([]);
    }
    console.log("final slides", finalSlides);
    await generateAndDownloadPPT(finalSlides);
  };

  const generateAndDownloadPPT = async (data) => {
    try {
      const response = await getPresentationFile(data);
      const byteCharacter = atob(response.data);
      const byteNumbers = new Array(byteCharacter.length);
      for (let i = 0; i < byteCharacter.length; i++) {
        byteNumbers[i] = byteCharacter.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      downloadButton.current.innerHTML = "";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.textContent = "Download PPTX";
      a.className =
        "p-4 bg-transparent text-blue-600 text-sm font-medium rounded-xl cursor-pointer ml-5";
      a.download = `${proposalSettings.client}_proposal.pptx`;
      downloadButton.current.appendChild(a);
      a.click();
    } catch (error) {
      console.log(error);
    }
  };

  const handleGenerateClick = async () => {
    setIsLoading(true);
    let finalProposalData = {};
    for (const section of Object.keys(slides)) {
      const slidesText = [];
      slides[section].forEach((slide, index) => {
        slidesText.push(
          `Slide ${index + 1}: ${slide.description} Id of the slide is: ${
            slide.slideId
          }`
        );
      });
      const sectionData = await getSectionFromPrompt(
        slidesText.join("\n"),
        section
      );
      finalProposalData = {
        ...finalProposalData,
        [section]: sectionData.data,
      };
    }
    if (isAborted.current) {
      isAborted.current = false;
      return;
    }
    console.log("proposal structure", finalProposalData);
    await preprocessAndGenerateFile(finalProposalData);
    setIsLoading(false);
  };

  const getSectionFromPrompt = async (slidesText, section) => {
    try {
      return await generateLayoutFromPrompt({
        prompt: input,
        slidesPrompt: `Here are the slides i want you to create: ${slidesText}`,
        section: section,
        industry: proposalSettings.industry,
        client: proposalSettings.client,
      });
    } catch (error) {
      console.error("Error generating presentation", error);
      showErrorMessage("Error while generating one of the proposal section");
    }
  };

  const getRelevantCaseStudies = async () => {
    try {
      const response = await getCaseStudiesFromPrompt(
        `${input}. Client the proposal is for is: ${proposalSettings.client} and the industry is: ${proposalSettings.industry}`
      );
      const caseStudies = response["Case studies"]?.images.map((slide) => ({
        id: uuidv4(),
        image: `data:image/png;base64,${slide}`,
      }));
      setCaseStudiesSlides(caseStudies);
    } catch (error) {
      console.log("Error getting case studies", error);
      enqueueSnackbar("Error while trying to fetch relevant case studies", {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
    }
  };

  const abortFileGeneration = () => {
    isAborted.current = true;
    setIsLoading(false);
  };

  const handleGenerateDescriptionsClick = async () => {
    if (!proposalSettings.client || !proposalSettings.industry) {
      showErrorMessage("Please fill in the client details before proceeding");
      return;
    }
    setSlides([]);
    nextStep();
    getRelevantCaseStudies();
    for (const section of dynamicSections) {
      try {
        const response = await getDescriptionsFromPrompt(section);
        let responseJson = {};
        console.log(response);
        Object.keys(response?.data).forEach((section) => {
          const sectionSlides = response?.data?.[section];
          if (!sectionSlides) return;
          responseJson[section] = sectionSlides?.length
            ? sectionSlides.map((slide) => ({
                id: uuidv4(),
                ...slide,
              }))
            : [sectionSlides];
        });
        setSlides((prev) => ({
          ...prev,
          ...responseJson,
        }));
      } catch (error) {
        console.error("Error getting descriptions", error);
        showErrorMessage(
          "Error while trying to get outline for the proposal section"
        );
      }
    }
  };

  const getDescriptionsFromPrompt = async (section) => {
    try {
      return await generateDescriptionsFromPrompt({
        prompt: `${input}. Return only slides for the section with the following id: ${section}. ${
          proposalSettings.client
            ? "Client for this proposal is: " +
              proposalSettings.client +
              " and is part of following industry: " +
              proposalSettings.industry
            : ""
        }`,
      });
    } catch (error) {
      setStep(1);
      showErrorMessage("Error while generating descriptions");
      console.error("Error generating descriptions", error);
    }
  };

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    let base64String;
    if (file) {
      try {
        base64String = await fileToBase64(file);
        console.log(base64String); // This is the Base64 string
      } catch (error) {
        console.error("Error converting file:", error);
      }
    }
    setProposalSettings({
      ...proposalSettings,
      logoFile: file,
      logoBase64: base64String,
    });
  };

  const handleCardDeleteClick = (section, index) => {
    const newSlides = { ...slides };
    newSlides[section].splice(index, 1);
    setSlides(newSlides);
  };

  const handleCardDescriptionChange = (section, index, value) => {
    setSlides((prevSlides) => ({
      ...prevSlides,
      [section]: prevSlides[section].map((slide, i) =>
        i === index ? { ...slide, description: value } : slide
      ),
    }));
  };

  const handleTextareaChange = (e) => {
    setInput(e.currentTarget.textContent);
  };

  const nextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handleElixirrValueSlideClick = (e) => {
    console.log(e.currentTarget.id);
    const slideId = e.currentTarget.id;
    const currentSlides = elixirrValueSelectedSlides;
    if (currentSlides.includes(slideId)) {
      console.log("removing slide");
      setElixirrValueSelectedSlides((prev) =>
        prev.filter((selectedSlide) => selectedSlide !== slideId)
      );
      return;
    }
    setElixirrValueSelectedSlides((prev) => [...prev, slideId]);
  };

  const handleCaseStudiesSlideClick = (e) => {
    console.log(e.currentTarget.id);
    const slideId = e.currentTarget.id;
    const currentSlides = caseStudiesSelectedSlides;
    if (currentSlides.includes(slideId)) {
      console.log("removing slide");
      setCaseStudiesSelectedSlides((prev) =>
        prev.filter((selectedSlide) => selectedSlide !== slideId)
      );
      return;
    }
    setCaseStudiesSelectedSlides((prev) => [...prev, slideId]);
  };

  const handleSubmitInitialDescription = () => {
    console.log(input);
    if (!input) {
      showErrorMessage(
        "Please enter a description for the proposal before proceeding"
      );
      return;
    }
    nextStep();
  };

  const activeSection = Object.keys(sectionMapper)[step - 2];

  console.log(caseStudiesSlides);

  return (
    <div className="h-screen flex bg-slate-100 bg-[radial-gradient(at_100%_100%,_hsla(228,_100%,_88%,_1)_0px,_transparent_50%),_radial-gradient(at_0%_100%,_hsla(226,_100%,_91%,_1)_0px,_transparent_50%)] w-full p-4 pb-6 overflow-hidden">
      {isLoading && (
        <div className="fixed top-0 left-0 z-50 w-full h-full bg-slate-800 bg-opacity-50 backdrop-blur-sm flex flex-col justify-center items-center">
          <Loader className="w-14 h-14 text-white animate-spin -mt-3" />
          <p className="text-white mt-2 text-lg font-medium">
            Generating proposal file please wait...
          </p>
          <Button
            onClick={abortFileGeneration}
            size="small"
            type="ghost"
            className=" mt-4"
          >
            Cancel
          </Button>
        </div>
      )}
      <div className="bg-white w-80 rounded-xl p-2 shadow-sm h-[100%]">
        <div className="flex w-52 h-24 justify-center rounded-lg items-center m-auto mb-4 z-10">
          <img
            src="/logo_sign.png"
            alt="cards"
            className="w-9 h-9 -ml-3 -mt-1"
          />
          <h2 className="text-left ml-2 text-lg font-semibold text-slate-700">
            Proposal AI
          </h2>
        </div>
        <div className="flex-col justify-between items-center m-auto w-full gap-4">
          <SidebarButton
            label="Describe Proposal"
            activeStep={step}
            step={0}
            handleSidebarButtonClick={() => setStep(0)}
          />
          <SidebarButton
            label="Client Details"
            activeStep={step}
            step={1}
            handleSidebarButtonClick={() => setStep(1)}
          />
          <p className="text-left text-slate-400 text-xs font-bold pl-5 mt-4 mb-1">
            SECTIONS
          </p>
          {Object.keys(sectionMapper).map((section, index) => (
            <SidebarButton
              label={sectionMapper[section]}
              disabled={JSON.stringify(slides) === "{}"}
              activeStep={step}
              step={index + 2}
              handleSidebarButtonClick={() => setStep(index + 2)}
            />
          ))}
        </div>
      </div>
      <div className="w-4/5 h-full rounded-xl p-2 ml-4">
        <div
          className={`flex flex-col align-middle justify-start w-4/5 max-w-[900px] min-w-[600px] m-auto mt-0`}
        >
          {step === 0 && (
            <>
              <h1 className="text-xl font-semibold mb-4 mt-24 font-sans text-slate-500">
                Lets start by describing your proposal ✨
              </h1>
              <div className="flex items-end gap-2 bg-white shadow-md rounded-2xl mt-2 relative ">
                <div
                  ref={initialInputRef}
                  contentEditable
                  className={`resize-none text-left flex-1 border-none p-4 rounded-2xl min-h-52 max-h-[70vh] overflow-auto pb-14`}
                  onInput={handleTextareaChange}
                  placeholder="Describe the proposal in a few sentences..."
                />
                <Button
                  size="small"
                  onClick={handleSubmitInitialDescription}
                  className="bg-blue-500 text-white right-4 bottom-4 absolute border-2 border-white"
                >
                  <ArrowUp className="w-7 h-6 py-0.5" />
                </Button>
              </div>
            </>
          )}
          {step === 1 && (
            <ClientDetails
              proposalSettings={proposalSettings}
              setProposalSettings={setProposalSettings}
              handleUploadLogo={handleUploadLogo}
              handleGenerateDescriptionsClick={handleGenerateDescriptionsClick}
            />
          )}

          {step >= 2 && (
            <div>
              {activeSection === "valuePropositionSection" ? (
                <div className="pt-5">
                  <div className="text-left mb-11">
                    <h2 className="text-xl font-semibold text-slate-600 w-full text-left">
                      Elixirr Value Proposition Section
                    </h2>
                    <p className="text-slate-500">
                      Please select which of the standard “About Elixirr” slides
                      you would like to include in your proposal. You can edit
                      the content on each slide once your proposal is generated.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-5">
                    {ELIXIRR_VALUE_SLIDES.map((slide) => (
                      <div
                        id={slide.id}
                        onClick={handleElixirrValueSlideClick}
                        className="shadow-sm border bg-white border-slate-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200"
                      >
                        <img
                          src={slide.thumbnail}
                          alt="cards"
                          className="w-full h-max object-cover"
                        />
                        <div className="p-3 bg-white flex justify-between items-end">
                          <p className="text-sm text-left font-semibold text-slate-600">
                            {slide.title}
                          </p>
                          <input
                            checked={elixirrValueSelectedSlides.includes(
                              slide.id
                            )}
                            type="checkbox"
                            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          ></input>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={nextStep}
                    className={`mt-4 w-fit ml-auto float-end`}
                  >
                    Next Section
                  </Button>
                </div>
              ) : activeSection === "caseStudiesSection" ? (
                <div className="pt-5">
                  <div className="text-left mb-11">
                    <h2 className="text-xl font-semibold text-slate-600 w-full text-left">
                      Case Studies Section
                    </h2>
                    <p className="text-slate-500">
                      Please select which of the suggested case study slides you
                      would like to include in your proposal.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-5 max-h-[70vh] overflow-auto bg-slate-50 rounded-xl p-4 bg-opacity-65">
                    {caseStudiesSlides.map((slide) => (
                      <div
                        id={slide.id}
                        onClick={handleCaseStudiesSlideClick}
                        className="shadow-sm border bg-white border-slate-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200"
                      >
                        <img
                          src={slide.image}
                          alt="cards"
                          className="w-full h-max object-cover"
                        />
                        <div className="p-3 bg-white flex justify-between items-end">
                          <p className="text-sm text-left font-semibold text-slate-600">
                            {slide.title}
                          </p>
                          <input
                            checked={caseStudiesSelectedSlides.includes(
                              slide.id
                            )}
                            type="checkbox"
                            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          ></input>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center mt-4">
                    <Button
                      onClick={handleGenerateClick}
                      className="w-fit ml-auto float-end"
                      startIcon={
                        <Loader
                          className={`${isLoading ? "animate-spin" : ""}`}
                        />
                      }
                    >
                      Generate Proposal
                    </Button>
                    <div ref={downloadButton}></div>
                  </div>
                </div>
              ) : slides[activeSection] ? (
                <>
                  <CardList
                    slides={slides}
                    setSlides={setSlides}
                    onDragEnd={onDragEnd}
                    handleAddSlide={handleAddSlide}
                    onCardChange={handleCardDescriptionChange}
                    onCardDelete={handleCardDeleteClick}
                    section={activeSection}
                  />
                  <Button
                    onClick={nextStep}
                    className={`mt-4 w-fit ml-auto float-end mr-56`}
                  >
                    Next Section
                  </Button>
                </>
              ) : (
                <div role="status" class="animate-pulse mt-5">
                  <div class="h-8 w-48 bg-white rounded-2xl mb-20"></div>
                  <div class="h-24 bg-white rounded-2xl mb-2.5"></div>
                  <div class="h-24 bg-white rounded-2xl mb-2.5"></div>
                  <div class="h-24 bg-white rounded-2xl mb-2.5"></div>
                  <div class="h-24 bg-white rounded-2xl "></div>
                </div>
              )}
            </div>
          )}

          {/*    {Object.keys(slides).length > 1 && (
            <Button
              onClick={() => generateAndDownloadPPT(slides)}
              className="mt-4 w-fit ml-auto"
              disabled={isLoading}
            >
              Download PPT
            </Button>
          )} */}
        </div>
      </div>
    </div>
  );
}
